
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { Plan } from "@/types/subscription";
import { useStripeCheckout } from "@/hooks/subscription/useStripeCheckout";

interface PlanCardProps {
  plan: Plan;
  isYearly: boolean;
  selectedOrgId: string;
  onSelectPlan: (planId: string) => void;
}

export const PlanCard = ({ 
  plan, 
  isYearly, 
  selectedOrgId,
  onSelectPlan 
}: PlanCardProps) => {
  const { isLoading, hasError, handleCheckout } = useStripeCheckout();

  const handleSelect = async () => {
    if (!selectedOrgId) return;
    await handleCheckout(plan.id, selectedOrgId, isYearly);
    onSelectPlan(plan.id);
  };

  return (
    <div className={`bg-white rounded-lg shadow-md overflow-hidden border transition-all hover:shadow-lg ${
      plan.isPopular ? 'border-custom-primary transform scale-105 z-10' : 'border-gray-200'
    }`}>
      {plan.isPopular && (
        <div className="bg-custom-primary text-white text-center py-1 text-sm font-medium">
          Plan recommandé
        </div>
      )}
      
      <div className="p-6">
        <h3 className="text-xl font-bold text-custom-dark mb-1">{plan.displayName}</h3>
        <p className="text-gray-500 text-sm mb-4">
          Idéal pour {plan.name === 'standard' ? 'les petites équipes' : 
                    plan.name === 'professional' ? 'les entreprises en croissance' : 
                    'les grandes organisations'}
        </p>
        
        <div className="mb-6">
          <p className="text-3xl font-bold text-custom-dark">
            {isYearly ? plan.priceYearly / 12 : plan.price}€
            <span className="text-base font-normal text-gray-500">/mois</span>
          </p>
          {isYearly && (
            <p className="text-sm text-gray-500">
              Facturé annuellement à {plan.priceYearly}€
            </p>
          )}
        </div>
        
        <Button 
          onClick={handleSelect}
          className={`w-full ${
            plan.isPopular
              ? 'bg-custom-primary hover:bg-custom-dark'
              : 'bg-custom-dark hover:bg-custom-dark/90'
          }`}
          disabled={isLoading || !selectedOrgId}
        >
          {isLoading ? "Traitement..." : "Sélectionner ce plan"}
        </Button>
        
        {hasError && (
          <div className="mt-2 text-amber-600 text-sm flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" />
            <span>Erreur lors de l'initialisation du paiement</span>
          </div>
        )}

        <ul className="mt-6 space-y-3">
          {plan.features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <CheckCircle2 
                className={`h-5 w-5 mr-2 flex-shrink-0 ${
                  feature.included ? 'text-green-500' : 'text-gray-300'
                }`} 
              />
              <span className={feature.included ? 'text-gray-700' : 'text-gray-400'}>
                {feature.name}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

